import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabaseAuth, safeGetSession } from '../../services/supabase';
import { useI18n } from '../../context/I18nContext';
import { authColors, shadows } from '../../styles/colors';
import { purgeSupabaseAuthStorage, safeLocalSignOut } from '../../utils/authReset';
import { clearAuthSnapshot } from '../../services/authStore';
import { clearPasswordRecoveryPending } from '../../utils/passwordRecoveryStorage';
import Spinner from '../ui/Spinner';
import AuthLayout from './AuthLayout';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  // Standard: Handle browser back button - redirect to login instead of /app
  useEffect(() => {
    // Push current state to create a history entry we can intercept
    window.history.pushState(
      { page: 'reset-password', preventBack: true },
      '',
      window.location.href
    );

    const handlePopState = () => {
      // When user clicks browser back button, redirect to login
      // This prevents redirect to /app since user is authenticated
      clearPasswordRecoveryPending();
      try {
        purgeSupabaseAuthStorage();
        clearAuthSnapshot();
        supabaseAuth.auth.signOut().catch(() => {});
      } catch {
        // ignore
      }
      // Use replace to avoid adding to history
      window.location.replace('/login');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    // Check if we have a valid session (from OTP verification)
    const checkSession = async () => {
      // If email is provided, we're coming from OTP flow
      if (email) {
        // Check if we have a valid session (OTP verification should have created one)
        const { data: { session }, error: sessionError } = await safeGetSession(3000);
        if (!sessionError && session) {
          setIsValidSession(true);
        } else {
          // Also check hash for backward compatibility with magic links
          const hasHash = location.hash && location.hash.length > 1;
          if (hasHash) {
            const hashParams = new URLSearchParams(location.hash.substring(1));
            const type = hashParams.get('type');
            if (type === 'recovery') {
              // Wait for Supabase to process hash
              setTimeout(async () => {
                const { data: { session: delayedSession }, error: delayedError } = await safeGetSession(3000);
                setIsValidSession(!delayedError && !!delayedSession);
              }, 1000);
            } else {
              setIsValidSession(false);
            }
          } else {
            setIsValidSession(false);
          }
        }
      } else {
        // No email param - check hash for magic link compatibility
        const hasHash = location.hash && location.hash.length > 1;
        if (hasHash) {
          const hashParams = new URLSearchParams(location.hash.substring(1));
          const type = hashParams.get('type');
          if (type === 'recovery') {
            setTimeout(async () => {
              const { data: { session: delayedSession }, error: delayedError } = await safeGetSession(3000);
              setIsValidSession(!delayedError && !!delayedSession);
            }, 1000);
          } else {
            setIsValidSession(false);
          }
        } else {
          setIsValidSession(false);
        }
      }
    };

    checkSession();
  }, [location, email]);

  useEffect(() => {
    // If password is reset successfully, redirect to login after showing success message
    if (success) {
      const timer = setTimeout(() => {
        // Use window.location to ensure clean state (user is already signed out)
        window.location.href = '/login';
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      setLoading(false);
      return;
    }

    // Password validation: minimum 8 characters, max 128, not just whitespace, uppercase, lowercase, digit
    const trimmedPassword = password.trim();
    
    if (trimmedPassword.length === 0) {
      setError(t('auth.passwordNotWhitespace'));
      setLoading(false);
      return;
    }

    if (trimmedPassword.length < 8) {
      setError(t('auth.minPasswordLength'));
      setLoading(false);
      return;
    }

    if (password.length > 128) {
      setError(t('auth.passwordTooLong'));
      setLoading(false);
      return;
    }

    // Check if password contains at least one uppercase letter
    if (!/[A-Z]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoUppercase'));
      setLoading(false);
      return;
    }

    // Check if password contains at least one lowercase letter
    if (!/[a-z]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoLowercase'));
      setLoading(false);
      return;
    }

    // Check if password contains at least one digit
    if (!/[0-9]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoDigit'));
      setLoading(false);
      return;
    }

    try {
      // Update password using Supabase
      const { error: updateError } = await supabaseAuth.auth.updateUser({
        password: password,
      });

      if (updateError) {
        // Check for specific error messages and translate them
        const errorMessage = updateError.message || '';
        if (errorMessage.toLowerCase().includes('new password should be different') || 
            errorMessage.toLowerCase().includes('password should be different')) {
          setError(t('auth.passwordSameAsOld'));
        } else {
          setError(errorMessage || t('auth.resetPasswordError'));
        }
        setLoading(false);
      } else {
        // Password reset successful - sign out user and redirect to login
        setSuccess(true);
        setLoading(false);
        clearPasswordRecoveryPending();
        // Clean up auth state manually (standard SaaS: redirect to login, not landing)
        try {
          purgeSupabaseAuthStorage();
          clearAuthSnapshot();
          // Best-effort remote sign out (non-blocking)
          supabaseAuth.auth.signOut().catch(() => {});
          safeLocalSignOut().catch(() => {});
        } catch {
          // Ignore cleanup errors, continue with redirect
        }
      }
    } catch (err) {
      setError(t('auth.resetPasswordError'));
      setLoading(false);
    }
  };

  // Loading state while checking session validity
  if (isValidSession === null) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center">
            <Spinner size="lg" label={t('common.loading')} className="justify-center" />
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Invalid session
  if (isValidSession === false) {
    clearPasswordRecoveryPending();
    return (
      <AuthLayout>
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.resetPasswordTitle')}
            </h2>
            <p className="text-red-600">
              {t('auth.invalidResetLink')}
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className={`w-full ${authColors.primaryButton} ${shadows.medium} focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          >
            {t('auth.backToLogin')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.resetPasswordSuccess')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('auth.resetPasswordSuccessMessage')}
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Form state
  return (
    <AuthLayout>
      <div className="bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.resetPasswordTitle')}
          </h2>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.newPassword')}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                placeholder={t('auth.newPasswordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">{t('auth.passwordRequirements')}</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.confirmNewPassword')}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                placeholder={t('auth.confirmNewPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full ${authColors.primaryButton} ${shadows.medium} focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? t('auth.resettingPassword') : t('auth.resetPassword')}
          </button>

          <button
            type="button"
            onClick={() => {
              // Standard: redirect to login when going back from password reset
              // Clean up session first to prevent auto-redirect to /app
              clearPasswordRecoveryPending();
              try {
                purgeSupabaseAuthStorage();
                clearAuthSnapshot();
                supabaseAuth.auth.signOut().catch(() => {});
              } catch {
                // ignore
              }
              window.location.href = '/login';
            }}
            className={`w-full ${authColors.authLink} py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors mt-3`}
          >
            {t('auth.backToLogin')}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;

